// The date window on the Lead Overview, and the quick ranges above it.
//
// WHY THIS IS ITS OWN MODULE. "Today" is a surprisingly easy thing to get
// wrong, and every way of getting it wrong looks like a quiet day rather than a
// bug: an end boundary at midnight loses the whole of the last day, a range
// built from UTC shows an Indian business yesterday's evening as today's
// morning, and a "last 7 days" that counts 7 from midnight actually spans 8
// calendar days. Each of those is one line, and none of them throws.
//
// WHAT THE SERVER GETS. Bare `YYYY-MM-DD` strings, built from the VIEWER'S OWN
// calendar. CaptureFilters on the server reads a bare date as that whole local
// day — start at 00:00, end at 23:59:59.999 — which is the same parser the
// Leads list uses, so "today" means one day on both screens rather than two
// definitions a day apart.

/** The quick ranges, in the order the bar offers them. */
export type RangeId = 'today' | 'last7' | 'thisMonth' | 'last30' | 'all' | 'custom';

export interface DateRange {
  /** `YYYY-MM-DD`, or '' for an open start. */
  startDate: string;
  /** `YYYY-MM-DD`, or '' for an open end. */
  endDate: string;
}

export const ALL_TIME: DateRange = { startDate: '', endDate: '' };

/** A local calendar date as `YYYY-MM-DD`. Never `toISOString`, which is UTC. */
export function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** N days before `from`, as a new Date. Negative counts go forward. */
const minusDays = (from: Date, days: number): Date => {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  d.setDate(d.getDate() - days);
  return d;
};

export interface QuickRange {
  id: RangeId;
  label: string;
  /** What the range actually covers, for the button's tooltip. */
  hint: string;
}

export const QUICK_RANGES: QuickRange[] = [
  { id: 'today', label: 'Today', hint: 'Midnight to now, on your own clock' },
  { id: 'last7', label: 'Last 7 days', hint: 'The last seven days including today' },
  { id: 'thisMonth', label: 'This month', hint: 'From the 1st of this month to today' },
  { id: 'last30', label: 'Last 30 days', hint: 'The last thirty days including today' },
  { id: 'all', label: 'All time', hint: 'Every lead and call ever recorded' },
];

/**
 * A quick range as concrete dates.
 *
 * `today` is passed in rather than read from the clock so this is testable and
 * so a screen left open overnight can be recomputed rather than silently
 * reporting yesterday.
 */
export function rangeFor(id: RangeId, today: Date = new Date()): DateRange {
  const end = ymd(today);
  switch (id) {
    case 'today':
      return { startDate: end, endDate: end };
    // INCLUDING today, so "last 7 days" is 7 calendar days and not 8. Counting
    // back a full 7 from today would span a week and a day, which quietly
    // inflates every figure on the screen against the previous week.
    case 'last7':
      return { startDate: ymd(minusDays(today, 6)), endDate: end };
    case 'last30':
      return { startDate: ymd(minusDays(today, 29)), endDate: end };
    case 'thisMonth':
      return { startDate: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), endDate: end };
    case 'all':
    default:
      return ALL_TIME;
  }
}

/**
 * Which quick range a window corresponds to, or 'custom'.
 *
 * Needed because the window can also be typed into the two date boxes, and a
 * bar with nothing lit while a filter is plainly applied reads as broken.
 */
export function rangeIdFor(range: DateRange, today: Date = new Date()): RangeId {
  if (!range.startDate && !range.endDate) return 'all';
  const match = QUICK_RANGES.find((q) => {
    if (q.id === 'all') return false;
    const r = rangeFor(q.id, today);
    return r.startDate === range.startDate && r.endDate === range.endDate;
  });
  return match ? match.id : 'custom';
}

/** True when the range asks for nothing — every lead and call. */
export const isAllTime = (r: DateRange): boolean => !r.startDate && !r.endDate;

/** Backwards is a real mistake somebody can type, and it silently returns nothing. */
export const isBackwards = (r: DateRange): boolean =>
  Boolean(r.startDate && r.endDate && r.startDate > r.endDate);

/** What the window covers, for the heading. Never "1 Jan – 1 Jan". */
export function describe(range: DateRange, today: Date = new Date()): string {
  if (isAllTime(range)) return 'All time';
  const id = rangeIdFor(range, today);
  const quick = QUICK_RANGES.find((q) => q.id === id);
  if (quick) return quick.label;

  const pretty = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };
  if (range.startDate && range.endDate) {
    return range.startDate === range.endDate
      ? pretty(range.startDate)
      : `${pretty(range.startDate)} – ${pretty(range.endDate)}`;
  }
  return range.startDate ? `Since ${pretty(range.startDate)}` : `Until ${pretty(range.endDate)}`;
}

/** Only the params that narrow anything — an empty string would filter to nothing. */
export function rangeParams(range: DateRange): Record<string, string> {
  const out: Record<string, string> = {};
  if (range.startDate) out.startDate = range.startDate;
  if (range.endDate) out.endDate = range.endDate;
  return out;
}
